import { Button, Stack, TextField, Typography } from '@mui/material'
import { apiGet, apiPost } from '../../../generic/Api_Functions'
import { useEffect, useState } from 'react'
import FileDownloadDoneIcon from '@mui/icons-material/FileDownloadDone'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { SmartDialog } from '../../../generic/utilities/SmartDialog'
import RoundedIconButton from '../../../Compass/Common/CompassButton/RoundedIconButton'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import AlertUI from '../../../generic/AlertUI'
import { useAuthorization } from '../../../generic/hooks/useAuthorization'
const FinishJobButton = (props) => {
  const { part, buttonVariant = 'text' } = props
  const [unfinishedJob, setUnfinishedJob] = useState(null)
  const [result, setResult] = useState(null)
  const { t_ } = useSmartTranslation()
  const [alertElement, showAlert] = AlertUI()
  const [alertElementAction, showAlertAction] = AlertUI()
  const [showActionDialog, setShowActionDialog] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [text, setText] = useState('')
  const [showError, setShowError] = useState(false)
  const isUserAuthorized = useAuthorization()

  useEffect(() => {
    if (showActionDialog) {
      setResult(null)
    }
  }, [showActionDialog])

  useEffect(() => {
    if (showConfirmDialog) {
      setShowError(false)
    }
  }, [showConfirmDialog])

  // Keyed on the part ID, not the object identity: callers that poll the part (traceability,
  // monitoring resource actions) hand down a NEW object every tick, and keying on `part`
  // would re-fetch the workflow history every second.
  const partId = part?.id
  useEffect(() => {
    const getUnfinishedPartWorkflowHistory = async () => {
      const response = await apiGet('getUnfinishedPartWorkflowHistory/' + partId)
      setUnfinishedJob(response)
    }
    if (partId) {
      getUnfinishedPartWorkflowHistory()
    }
  }, [partId])
  const onClick = async () => {
    await apiPost('finishJob/partWorkflowHistory/' + unfinishedJob.id + '/' + result).then(() => {
      showAlert({ severity: 'success', title: t_('Successfully requested') })
    }).catch((e) => {
      showAlert({
        title: '',
        message: e.message,
        severity: 'error'
      })
    })
  }

  const isMachineNameValid = () => {
    if (!unfinishedJob || !unfinishedJob.device || !unfinishedJob.device.name || !text) {
      return false
    }
    return text.toLocaleLowerCase() === unfinishedJob.device.name.toLocaleLowerCase()
  }

  // !part?.status → the part is still loading (the monitoring dialog seeds { id } first):
  // stay disabled until the full object arrives instead of flashing enabled.
  const finishJobDisabled =
    !part?.status ||
    (unfinishedJob && unfinishedJob?.dateStart == null) ||
    !isUserAuthorized('FINISH_JOB')

  return (
    <>
      {buttonVariant === 'touch'
        ? (
          <button
            type='button'
            className='mcx-touch-btn mcx-touch-btn--lg'
            data-testid='resource-action-finish-job'
            disabled={finishJobDisabled}
            onClick={() => setShowActionDialog(true)}
          >
            <FileDownloadDoneIcon sx={{ fontSize: 32 }} />
            <span className='mcx-touch-btn__label'>{t_('Finish job')}</span>
          </button>
          )
        : (
          <Button
            startIcon={<FileDownloadDoneIcon />}
            disabled={finishJobDisabled}
            onClick={() => setShowActionDialog(true)}
          >{t_('Finish job')}
          </Button>
          )}
      <SmartDialog
        cancelCallback={() => setShowActionDialog(false)}
        acceptCallback={() => {
          if (!result) {
            showAlertAction({ severity: 'error', title: t_('Select the result of the operation'), message: ' ' })
            return
          }
          setText('')
          setShowConfirmDialog(true)
        }}
        accept
        renderComponent={
          <>
            <Stack gap={2} sx={{ margin: '1rem' }}>

              <Typography variant='h6'>{t_('Select the result of the operation')}</Typography>
              {alertElementAction}
              <Stack sx={{ width: '100%' }} direction='row' gap={3} justifyContent='center'>
                <RoundedIconButton
                  selected={result && result === 'FAIL'}
                  backgroundColor='red'
                  icon={<CloseIcon style={{ transform: ' scale(2.2)' }} />}
                  onClick={() => { setResult('FAIL') }}
                />
                <RoundedIconButton
                  selected={result && result === 'OK'}
                  backgroundColor='green'
                  icon={<CheckIcon style={{ transform: ' scale(2.2)' }} />}
                  onClick={() => { setResult('OK') }}
                />
              </Stack>
            </Stack>
          </>
        }
        setOpen={showActionDialog}
      />

      <SmartDialog
        title={{ render: <Typography color='primary' variant='h4'>{t_('You are going to stop the job in the asset') + ' ' + unfinishedJob?.device?.name}</Typography> }}
        cancelCallback={() => setShowConfirmDialog(false)}
        acceptCallback={() => {
          if (isMachineNameValid()) {
            onClick()
            setShowActionDialog(false)
            setShowConfirmDialog(false)
          } else {
            setShowError(true)
          }
        }}
        accept
        renderComponent={
          <Stack gap={3} sx={{ marginTop: '1rem' }}>
            {alertElement}
            <Typography>{t_('Please insert asset name to confirm the action')}</Typography>
            <Stack direction='row' justifyContent='center' gap={3} alignItems='baseline'>
              <Typography color='primary' sx={{ textAlign: '' }}>{t_('Asset name')}</Typography>
              <TextField
                value={text || ''}
                onChange={event =>
                  setText(event.target.value)}
                error={showError && !isMachineNameValid()}
                helperText={text !== unfinishedJob?.device?.name ? t_('Introduce the asset name') : null}
                sx={{ width: 70, flexGrow: 1 }}
              />
            </Stack>
            {
              unfinishedJob?.taskReferenceList?.length > 0
                ? (
                  <Stack direction='column' sx={{ width: '100%' }} justifyContent='center' gap={3}>
                    <Typography>{t_('These tasks are not going to be executed')}</Typography>

                    {unfinishedJob?.taskReferenceList.map(element => {
                      return <Typography key={element.id}>{element.name + ' --- Program:' + element.programName}</Typography>
                    })}
                  </Stack>)
                : null
            }

          </Stack>

        }
        setOpen={showConfirmDialog}
      />
    </>
  )
}

export default FinishJobButton
